import React, { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Input, Textarea, Toggle } from "../../Components/Form";
import { additionalDataCapture as v } from "./validate";
import { Field, FieldArray } from "redux-form";
import { connect } from "react-redux";
import { agencyHasFeature, token } from "../../modules/auth";
import { LiveSearchSimple } from "../../Components/Form/LiveSearch/LiveSearchSimple";
import { createCompany } from "../../redux/actions";
import RepeatableLinkedEnquiries from "../../Components/Rfp/RepeatableLinkedEnquiries";
import { Select } from "../../ui";
import { AGENCY_PERMISSIONS } from "../../constants";
import { isRolePlatformAdmin } from "../../modules/permissions";

const mapStateToProps = state => ({
  token: token(state),
  canSuppressBriefNotifications: agencyHasFeature(
    state.auth,
    AGENCY_PERMISSIONS.CAN_SUPPRESS_BRIEF_NOTIFICATIONS
  ),
  isPlatformAdmin: isRolePlatformAdmin(state.auth),
  ...state.agencyBookingDesks,
});

const mapDispatchToProps = {
  createCompany,
};

const decisionMakingProcessPlaceholder = `Who is the decision maker?
<br/>              
What is the most important factor for making a decision?
<br/>         
Would you require a site visit?<br/><br/>`;

const probingQuestionsPlaceholder = `What is the previous history of the event?
<br/>
What is the purpose / objective?
<br/>
Who is attending?
<br/>
How often is this event held?`;

const AgencySpecificEnquiryData = ({
  form,
  company,
  formValues,
  editEnquiry = false,
  isSuperAgent,
  change,
  enquiry,
  token,
  setAwaitingRequests,
  agencyBookingDesks,
  canSuppressBriefNotifications,
  isPlatformAdmin,
}) => {
  const [selectedLinkedEnquiries, setSelectedLinkedEnquiries] = useState([]);
  const [linkedEnquiries, setLinkedEnquiries] = useState();

  useEffect(() => {
    if (enquiry && !enquiry?.is_parent_enquiry) {
      getLinkedEnquiries();
    }
  }, []);

  const handleLinkedEnquiriesChange = linkedEnquiry => {
    if (!selectedLinkedEnquiries.includes(linkedEnquiry)) {
      setSelectedLinkedEnquiries(prevEnquiries => [
        ...prevEnquiries,
        linkedEnquiry,
      ]);
    }
  };

  const getBookingDesksOptions = useCallback(() => {
    let options = [];
    if (agencyBookingDesks && Object.keys(agencyBookingDesks).length > 0) {
      Object.values(agencyBookingDesks).forEach(agencyDesks => {
        agencyDesks.forEach(desk => {
          options.push({
            text: desk.name,
            value: desk.id,
          });
        });
      });
    }
    options.sort((a, b) => a.text.localeCompare(b.text));
    return options;
  }, [agencyBookingDesks]);

  const getLinkedEnquiries = async () => {
    setAwaitingRequests(true);
    setLinkedEnquiries();
    await axios
      .get(`${window.API}/rfp/enquiry/${enquiry.id}/linked-enquiries`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        setAwaitingRequests(false);
        setLinkedEnquiries(data);
      })
      .catch(error => {
        setLinkedEnquiries();
        setAwaitingRequests(false);
        console.error(error);
      })
      .finally(() => {
        setAwaitingRequests(false);
      });
  };

  return (
    <>
      {isSuperAgent ? (
        <div className="row mb-2">
          <div className="col col-w-1/2">
            <Field
              label="Decision Making Process"
              name="decision_making_process"
              component={Textarea}
              rows={6}
              subTextHint={decisionMakingProcessPlaceholder}
            />
          </div>
          <div className="col col-w-1/2">
            <Field
              label="Probing Questions"
              name="probing_questions"
              component={Textarea}
              rows={6}
              subTextHint={probingQuestionsPlaceholder}
            />
          </div>
        </div>
      ) : null}
      <div className="row mt-10">
        <div className="col col-w-1/4">
          <Field
            name="estimated_value"
            label="Estimated Value"
            component={Input}
            validate={v.estimated_value}
            wrapperClassName="w-full mb-8"
          />
        </div>
        <div className="col col-w-1/4">
          <Field
            name="estimated_commission"
            label="Estimated Commission"
            component={Input}
            validate={v.estimated_commission}
            wrapperClassName="w-full mb-8"
          />
        </div>
      </div>
      <div className="row">
        <div className="col col-w-1/4">
          <Field
            name="share_with_venues"
            label="Share client name with the Venues?"
            component={Toggle}
            validate={v.share_with_venues}
            wrapperClassName="inline mb-8 xl:w-1/2"
          />
        </div>
        {canSuppressBriefNotifications || isPlatformAdmin ? (
          <div className="col col-w-1/4">
            <Field
              name="suppress_notifications"
              label="Supress All Notifications?"
              component={Toggle}
              validate={v.suppress_notifications}
              wrapperClassName="inline mb-8 xl:w-1/2"
            />
          </div>
        ) : null}
      </div>
      {company ? (
        <div className="py-4">
          {company &&
          !company.corporate_account &&
          isSuperAgent &&
          getBookingDesksOptions().length > 0 ? (
            <div className="row">
              <div className="col col-w-1/3 mb-5">
                <Field
                  name="agency_booking_desk_id"
                  label="Booking Desk"
                  component={Select}
                  options={getBookingDesksOptions()}
                  onChange={e => {
                    change(form, "agency_booking_desk_id", e.target.value);
                  }}
                  // validate={v.booking_desk_id}
                  wrapperClassName="inline mb-8 xl:w-1/2"
                />
              </div>
            </div>
          ) : null}

          <label className="mb-1 block font-normal font-heading text-sm uppercase">
            Linked Enquiries
          </label>
          {editEnquiry && enquiry && enquiry.is_parent_enquiry ? (
            <>
              <div className="row">
                <div className="col col-w-1/3">
                  <LiveSearchSimple
                    auth
                    multi
                    // label="Linked Enquiries"
                    wrapperClassName="flex flex-col"
                    multiSearchClass="max-w-full"
                    multiResultsClass="hidden"
                    getText={item =>
                      `${item.event_name} (RefID: ${item.ref_id})`
                    }
                    url={`${window.API}/rfp/enquiry/quick/search?companyId=${company.id}`}
                    onChange={item => handleLinkedEnquiriesChange(item)}
                  />
                </div>
              </div>
              <div className="mb-4">
                <div className="col col-w-full p-4">
                  <FieldArray
                    name="linked_enquiries"
                    props={{ change }}
                    values={formValues}
                    selectedLinkedEnquiries={selectedLinkedEnquiries}
                    component={RepeatableLinkedEnquiries}
                  />
                </div>
              </div>
            </>
          ) : null}
          {!editEnquiry ? (
            <>
              <div className="row">
                <div className="col col-w-1/3">
                  <LiveSearchSimple
                    auth
                    multi
                    wrapperClassName="flex flex-col"
                    multiSearchClass="max-w-full"
                    multiResultsClass="hidden"
                    getText={item =>
                      `${item.event_name} (RefID: ${item.ref_id})`
                    }
                    url={`${window.API}/rfp/enquiry/quick/search?companyId=${company.id}`}
                    onChange={item => handleLinkedEnquiriesChange(item)}
                  />
                </div>
              </div>
              <div className="mb-4">
                <div className="col col-w-full p-4">
                  <FieldArray
                    name="linked_enquiries"
                    props={{ change }}
                    values={formValues}
                    selectedLinkedEnquiries={selectedLinkedEnquiries}
                    component={RepeatableLinkedEnquiries}
                  />
                </div>
              </div>
            </>
          ) : null}
          {linkedEnquiries &&
            linkedEnquiries.map(linkedEnquiry => {
              return (
                <div className="row mb-4">
                  <div className="col col-w-1/3">
                    <div className="bg-light-grey p-4">
                      <div className="flex ml-4 justify-center items-center">
                        <div className="col">ID: {linkedEnquiry.id}</div>
                        <div className="col">
                          Ref ID: {linkedEnquiry.ref_id}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : null}
    </>
  );
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AgencySpecificEnquiryData);
